'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { asText, Content, isFilled, type ImageField } from '@prismicio/client';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';
import {
  CaseStudyDocument,
  TestimonialCarousel2SliceCaseStudiesFeedTwoColumns,
} from '../../../../prismicio-types';
import {
  brandOf,
  getCardExtras,
  industryOf,
  prepareCaseStudies,
} from '../shared/prepareCaseStudies';

interface CaseStudiesFeedTwoColumnsProps {
  slice: TestimonialCarousel2SliceCaseStudiesFeedTwoColumns;
  caseStudies: CaseStudyDocument[];
}

// How many additional cards each Load more click reveals — also the initial
// window. Matches the "up to 6 entries, then 6 more on click" spec.
const PAGE_SIZE = 6;

/**
 * "Case Studies Feed (Two Columns)" variation of CaseStudiesCarousel.
 *
 * Two-column feed of case study cards with progressive disclosure: renders
 * PAGE_SIZE cards first, then a Load more button appends PAGE_SIZE more each
 * click until every card is shown. The full list is fetched server-side
 * (identical pipeline to `fromMarkedCases`); pagination is client-side
 * windowing.
 */
const CaseStudiesFeedTwoColumns: FC<CaseStudiesFeedTwoColumnsProps> = ({
  slice,
  caseStudies,
}) => {
  const visibleCaseStudies = useMemo(
    () => prepareCaseStudies(caseStudies),
    [caseStudies],
  );

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const shown = visibleCaseStudies.slice(0, visibleCount);
  const hasMore = visibleCount < visibleCaseStudies.length;
  const loadMoreLabel = slice.primary.load_more_text?.trim() || 'Load more';

  const { title, subtitle, description } = slice.primary;
  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);

  return (
    <section
      data-slice-type="testimonial_carousel_2"
      data-slice-variation={slice.variation}
      // Section chrome matches HorizontalContentCards so the feed sits in the
      // same page-width rhythm as CaseIndustryShowcase's redesign variant.
      className="bg-white px-5 py-16 text-[#041118] md:px-[30px] md:py-20 lg:py-[70px]"
    >
      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-9">
        <div>
          {/* Two-tone headline: muted first line, bold second line — mirrors
              HorizontalContentCards' light-theme typography so this feed
              reads as part of the same redesign family. */}
          {(hasTitle || hasSubtitle) && (
            <h2 className="text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]">
              {hasTitle && <span className="text-[#aaa]">{asText(title)}</span>}
              {hasTitle && hasSubtitle && <br />}
              {hasSubtitle && (
                <span className="text-[#041118]">{asText(subtitle)}</span>
              )}
            </h2>
          )}
          {asText(description) && (
            <div className="mt-4 max-w-2xl text-base text-[#041118]/70">
              <PrismicRichText field={description} />
            </div>
          )}
        </div>

        {/* Nested flex column so the 48px gap between grid and Load more
            doesn't fight with the parent's 36px header→grid gap. */}
        <div className="flex flex-col gap-12">
          <div className="grid grid-cols-1 gap-[30px] md:grid-cols-2">
            {shown.map((cs) => (
              <CaseStudyFeedCard key={cs.id} caseStudy={cs} />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center md:justify-start">
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                // Matches OutlineCtaLink light-tone: static border/text;
                // only the label underlines on hover (via inner span).
                className={LOAD_MORE_BUTTON_CLASS}
              >
                <span className="group-hover:underline">{loadMoreLabel}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Mirrors OutlineCtaLink (light tone): static border/text with only the
// label underlined on hover. The card outer <Link> supplies the `group`, so
// this can stay a decorative <span> — nested <a>s aren't valid HTML.
const OUTLINE_CTA_SPAN_CLASS =
  'group inline-flex items-center justify-center gap-2 rounded-[8px] border border-[#041118] bg-transparent px-6 py-3 text-[16px] font-medium leading-[1.5] text-[#041118] transition-colors';

// Same visual language as OUTLINE_CTA_SPAN_CLASS but for a real <button>.
// `group` here (rather than `group-hover:*` on the label) means the button
// itself is the hover group, so it works standalone outside a card link.
const LOAD_MORE_BUTTON_CLASS =
  'group inline-flex items-center justify-center gap-2 rounded-[8px] border border-[#041118] bg-transparent px-6 py-3 text-[16px] font-medium leading-[1.5] text-[#041118] transition-colors';

/**
 * Card = hero image on top + a HorizontalContentCard body underneath. The
 * outer Link clips both with rounded-[16px] / overflow-hidden so the shared
 * card shell (`bg-[#f3f3f3] p-5 gap-[25px]`) blends with the image. The body
 * is intentionally shaped like CaseIndustryShowcase's redesignLogoCards
 * card — same badge treatment, same brand-logo-then-name fallback, same
 * outline CTA — so the two variations read as the same product family.
 */
const CaseStudyFeedCard: FC<{ caseStudy: CaseStudyDocument }> = ({
  caseStudy,
}) => {
  const { image, description } =
    caseStudy.data as Content.CaseStudyDocumentData;
  const href = caseStudy.url ?? '#';
  const brand = brandOf(caseStudy);
  // Falls back to the legacy "<Brand> - <Industry> industry" name pattern
  // when the top-level industry field isn't populated.
  const industry = industryOf(caseStudy);
  const { brand_logo, card_headline } = getCardExtras(caseStudy);

  return (
    <Link
      href={href}
      className="group flex flex-col overflow-hidden rounded-[16px] bg-[#f3f3f3] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.10)]"
    >
      {/* Hero — 16:9 to match the Figma spec (600 × 337.5). The image scales
          with the card width; the aspect ratio locks the height. A 30% black
          scrim (per Figma) sits on top for consistent contrast across images. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#e5e5e5]">
        <PrismicNextImage
          field={image}
          className="h-full w-full object-cover"
          alt=""
          fallbackAlt=""
        />
        <div aria-hidden className="absolute inset-0 bg-black/30" />
      </div>

      {/* Body — same shell as CaseIndustryShowcase redesignLogoCards; we drop
          the built-in radius so the outer Link's clipping shows through. */}
      <HorizontalContentCard className="flex-1 rounded-none">
        {industry && (
          <div className="flex items-center border-l border-[#4b0fea] py-1 pl-3">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#041118]">
              {industry}
            </span>
          </div>
        )}

        {isFilled.image(brand_logo) ? (
          <AutoGrayLogo
            field={brand_logo}
            className="h-8 w-auto max-w-[220px] self-start object-contain"
          />
        ) : (
          brand && (
            <p className="text-[24px] font-bold uppercase leading-[1.2] text-[#041118]">
              {brand}
            </p>
          )
        )}

        {isFilled.richText(card_headline) && (
          // Case-study hero headlines are StructuredText heading1; on a card
          // they must read as a 24px title, not a browser-default h1.
          <div className="text-[#041118]">
            <PrismicRichText
              field={card_headline}
              components={{
                heading1: ({ children }) => (
                  <h3 className="m-0 text-[20px] font-semibold leading-[1.4] md:text-[24px]">
                    {children}
                  </h3>
                ),
                heading2: ({ children }) => (
                  <h3 className="m-0 text-[20px] font-semibold leading-[1.4] md:text-[24px]">
                    {children}
                  </h3>
                ),
                paragraph: ({ children }) => (
                  <p className="m-0 text-[20px] font-semibold leading-[1.4] md:text-[24px]">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        )}

        {isFilled.richText(description) && (
          <div className="text-[16px] font-light leading-[1.5] text-[#041118] [&_p]:m-0 [&_p+p]:mt-2 md:text-[18px] line-clamp-4">
            <PrismicRichText field={description} />
          </div>
        )}

        <span className={`mt-auto self-start ${OUTLINE_CTA_SPAN_CLASS}`}>
          <span className="group-hover:underline">View case study</span>
        </span>
      </HorizontalContentCard>
    </Link>
  );
};

// Cache the detection result per image URL so we don't re-analyze the same
// logo every time a card mounts (React strict-mode double-mounts included).
const WHITE_LOGO_CACHE = new Map<string, Promise<boolean>>();

/**
 * Sample the image's opaque pixels and report whether the vast majority are
 * near-white (each RGB channel > 220). Prismic's CDN serves
 * `Access-Control-Allow-Origin: *`, so we can load the image with
 * `crossOrigin="anonymous"` and read its pixels via canvas. If CORS fails or
 * anything else throws, we treat the logo as non-white — safer than
 * accidentally graying out a colored logo.
 */
const detectMostlyWhite = (url: string): Promise<boolean> => {
  const cached = WHITE_LOGO_CACHE.get(url);
  if (cached) {
    return cached;
  }

  const promise = new Promise<boolean>((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Downsample to at most 200×200 — the ratio of whitish/opaque pixels
        // is stable at that resolution and it's cheap to process.
        const scale = Math.min(
          1,
          200 / Math.max(img.naturalWidth, img.naturalHeight, 1),
        );
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return resolve(false);
        }
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let opaque = 0;
        let whitish = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) {
            continue;
          } // ignore mostly-transparent pixels
          opaque++;
          if (data[i] > 220 && data[i + 1] > 220 && data[i + 2] > 220) {
            whitish++;
          }
        }
        resolve(opaque > 0 && whitish / opaque > 0.9);
      } catch {
        resolve(false); // canvas tainted or any other read failure
      }
    };
    img.onerror = () => resolve(false);
    img.src = url;
  });

  WHITE_LOGO_CACHE.set(url, promise);
  return promise;
};

// `brightness(0)` collapses every RGB channel to 0 (pure black + original
// alpha), then `invert(0.67)` maps 0 → 0.67 × 255 ≈ 170 = #AAA. So whatever
// the source colors were, opaque pixels become the exact target gray while
// the alpha channel (i.e. the logo's silhouette) is preserved.
const WHITE_LOGO_FILTER = 'brightness(0) invert(0.67)';

/**
 * PrismicNextImage that auto-detects a white/near-white logo and tones it
 * down to #AAA so it stays visible on the light card background. Non-white
 * logos are rendered untouched. Detection runs once per URL and is cached.
 */
const AutoGrayLogo: FC<{ field: ImageField; className?: string }> = ({
  field,
  className,
}) => {
  const url = field.url ?? undefined;
  const [isWhite, setIsWhite] = useState(false);

  useEffect(() => {
    if (!url) {
      return;
    }
    let cancelled = false;
    detectMostlyWhite(url).then((v) => {
      if (!cancelled) {
        setIsWhite(v);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <PrismicNextImage
      field={field}
      className={className}
      fallbackAlt=""
      style={isWhite ? { filter: WHITE_LOGO_FILTER } : undefined}
    />
  );
};

export default CaseStudiesFeedTwoColumns;
